import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@outpave/ds";
import CardStatusChip from "components/common/cards/CardStatusChip";
import type { DataColumn, RowAction } from "@outpave/ds";
import { GET_PENDING_APPROVAL_CARD } from "../../../graphql/card";
import moment from "moment";
import StatusType from "../../../enums/CardStatusType";
import { titleCase } from "title-case";
import { Typography } from "@mui/material";
import { renderCardTypeIcon } from "components/admin/cards/RenderCardIcon";
import { formatCashFull } from "../../../utils/formatCash";
import { DEFAULT_PAGINATION_SIZE } from "../../common/Constants";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FancyHoverComponent from "../../common/FancyHover";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import type { CardFilterCriteria } from "utils/cards/cardFilters";
import { isCardPendingTerms } from "utils/cards/cardRowData";
import type { CardPolicyLimits } from "utils/cards/cardRowData";
import { surnameFirstName } from "utils/fullName";

type Data = {
  id: string;
  userName: string;
  cardStatus: string;
  pendingTerms: boolean;
  type: string;
  expiration: string;
  transactionLimit: string;
  dailyLimit: string;
  weeklyLimit: string;
  monthlyLimit: string;
  totalLimit: string;
  merchants: string[];
  pendingRequest?: CardNode["pendingRequest"];
  currency: string;
};

type CardPolicy = CardPolicyLimits & {
  card_expiration_date?: string | null;
  merchants?: { name: string }[] | null;
  status?: string;
};

type CardNode = {
  id: string;
  status: StatusType;
  type: string;
  user: {
    firstName: string;
    lastName?: string | null;
    acceptedTerms?: boolean | null;
  };
  activePolicy?: CardPolicy | null;
  pendingRequest?: {
    id: string;
    requestStatus: string;
    requestType: string;
    requestData?: CardPolicy | null;
  } | null;
  currency: string;
};

type PendingCardApprovalQueryResult = {
  pendingCardApproval?: { edges?: { node: CardNode }[] };
};

type PendingCardApprovalProps = {
  handleOpenCardDetails: (id: string) => void;
  refreshCards: number;
  filterData: CardFilterCriteria;
};

const NO_LIMIT = "No Limit";

type LimitKey =
  | "transaction_limit"
  | "daily_limit"
  | "weekly_limit"
  | "monthly_limit"
  | "total_limit";

/** Pending requests win over the active policy, which only applies to an already active card */
const resolveLimit = (node: CardNode, limit: LimitKey): string => {
  const requested = node.pendingRequest?.requestData?.[limit];
  if (requested) return formatCashFull(requested, node.currency);

  const active = node.activePolicy?.[limit];
  if (node.status === StatusType.ACTIVE && active)
    return formatCashFull(active, node.currency);

  return NO_LIMIT;
};

const resolveMerchants = (node: CardNode): string[] => {
  const merchants = node.pendingRequest?.requestData?.merchants?.length
    ? node.pendingRequest.requestData.merchants
    : node.activePolicy?.merchants?.length
      ? node.activePolicy.merchants
      : [];

  return merchants.map((merchant) =>
    titleCase((merchant.name || "").toLowerCase()),
  );
};

const extractCardApprovals = (data: PendingCardApprovalQueryResult): Data[] =>
  (data.pendingCardApproval?.edges ?? []).map(({ node }) => ({
    id: node.id,
    userName: surnameFirstName(node.user),
    cardStatus: node.status,
    pendingTerms: isCardPendingTerms(node),
    type: node.type,
    expiration:
      node.status === StatusType.PENDING &&
      node.pendingRequest?.requestData?.card_expiration_date
        ? moment(node.pendingRequest.requestData.card_expiration_date).format(
            "MMMM YYYY",
          )
        : node.status === StatusType.ACTIVE &&
            node.activePolicy?.card_expiration_date
          ? moment(node.activePolicy.card_expiration_date).format("MMMM YYYY")
          : "-",
    transactionLimit: resolveLimit(node, "transaction_limit"),
    dailyLimit: resolveLimit(node, "daily_limit"),
    weeklyLimit: resolveLimit(node, "weekly_limit"),
    monthlyLimit: resolveLimit(node, "monthly_limit"),
    totalLimit: resolveLimit(node, "total_limit"),
    merchants: resolveMerchants(node),
    pendingRequest: node.pendingRequest,
    currency: node.currency,
  }));

const PendingCardApprovalTable: React.FC<PendingCardApprovalProps> = (
  props,
) => {
  const { handleOpenCardDetails, refreshCards, filterData } = props;
  const { t } = useTranslation();

  const [defaultPaginationVariables, setDefaultPaginationVariables] = useState(
    {},
  );

  useEffect(() => {
    if (filterData && Object.keys(filterData).length > 0) {
      setDefaultPaginationVariables({
        first: DEFAULT_PAGINATION_SIZE,
        after: undefined,
        searchTerm: filterData.searchTerm,
      });
    }
  }, [filterData]);

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_PENDING_APPROVAL_CARD,
      initialVariables: defaultPaginationVariables,
      extractData: extractCardApprovals,
      refetchIndex: refreshCards,
    });

  const viewCardRequest = (cardId: string) => {
    handleOpenCardDetails(cardId);
  };

  const columns: DataColumn<Data>[] = useMemo(
    () => [
      {
        id: "userName",
        header: t("Name"),
        cell: (approval) => approval.userName,
      },
      {
        id: "cardStatus",
        header: t("Card Status"),
        cell: (approval) => {
          return (
            <CardStatusChip
              status={approval.cardStatus}
              pendingTerms={approval.pendingTerms}
            />
          );
        },
      },
      {
        id: "expiration",
        header: t("Expiration"),
        cell: (approval) => approval.expiration,
      },
      {
        id: "transactionLimit",
        header: t("Transaction Limit"),
        cell: (approval) => approval.transactionLimit,
      },
      {
        id: "dailyLimit",
        header: t("Daily Limit"),
        cell: (approval) => approval.dailyLimit,
      },
      {
        id: "weeklyLimit",
        header: t("Weekly Limit"),
        cell: (approval) => approval.weeklyLimit,
      },
      {
        id: "monthlyLimit",
        header: t("Monthly Limit"),
        cell: (approval) => approval.monthlyLimit,
      },
      {
        id: "totalLimit",
        header: t("Total Limit"),
        cell: (approval) => approval.totalLimit,
      },
      {
        id: "merchants",
        header: t("Merchants"),
        cell: (approval) =>
          approval.merchants.length < 2 ? (
            <Typography variant="body1">
              {approval.merchants[0] ?? "-"}
            </Typography>
          ) : (
            <FancyHoverComponent
              title={t("Merchants")}
              data={approval.merchants}
            >
              <Typography variant="subtitle2">
                {t("Various merchants")}
              </Typography>
            </FancyHoverComponent>
          ),
      },
      {
        id: "viewRequest",
        header: t("Request Type"),
        cell: (approval) =>
          renderCardTypeIcon(approval.pendingRequest?.requestType, t),
      },
    ],
    [t],
  );

  const rowActions = (approval: Data): RowAction[] => [
    {
      label: t("View Approval Details"),
      icon: <VisibilityIcon fontSize="small" />,
      onClick: () => viewCardRequest(approval.id),
    },
  ];

  return (
    <DataTable<Data>
      columns={columns}
      rows={dataArray}
      getRowKey={(approval) => approval.id}
      loading={displayLoading}
      isFetchingMore={isInfiniteScrollFetching}
      onRowClick={(approval) => viewCardRequest(approval.id)}
      onScroll={handleScroll}
      emptyState={t("No pending card approvals \u{1F389}")}
      rowActions={rowActions}
      rowActionsLabel={t("RowActions")}
      loadingMoreText={t("Loading more cards")}
      stickyHeader
    />
  );
};

export default PendingCardApprovalTable;
